import { DateTime } from 'luxon'
import { Platform, type PlatformScores, SuspectScore, type SuspectScoreKey } from 'models'
import type { IdEventV4 } from 'models/event'

import { SMART_SIGNAL_DEFINITIONS } from './definitions'

export const WEIGHTS_PROPAGATION_BUFFER_MS = 5 * 60 * 1000

type SignalDefinition = (typeof SMART_SIGNAL_DEFINITIONS)[number]

export type SuspectScoreContribution = {
  definition: SignalDefinition
  contribution: number
}

export type SuspectScoreBreakdown = {
  contributing: SuspectScoreContribution[]
  notContributing: SuspectScoreContribution[]
  total: number
  reconciles: boolean
  weightsChangedSinceEvent: boolean
  weightsUpdatedAt?: Date
}

const SDK_PLATFORM_TO_WEIGHTS_PLATFORM: Record<string, Platform | undefined> = {
  js: Platform.Browser,
  android: Platform.Android,
  ios: Platform.Ios,
}

const CONFIDENCE_KEY_SUFFIX = { low: 'LowConfidence', medium: 'MediumConfidence', high: 'HighConfidence' } as const

function firedWeightKeys(signalKey: string, e: IdEventV4, weights: PlatformScores): SuspectScoreKey[] {
  switch (signalKey) {
    case 'bot-detection':
      return e.bot === 'bad' ? [SuspectScore.BadBots] : []

    case 'vpn': {
      if (weights.vpnWeightsMode === 'confidence') {
        return e.vpn === true ? [`vpn${CONFIDENCE_KEY_SUFFIX[e.vpn_confidence ?? 'low']}`] : []
      }
      const methods = e.vpn_methods ?? {}
      return [
        methods.timezone_mismatch && SuspectScore.VpnTimezone,
        methods.public_vpn && SuspectScore.VpnPublic,
        methods.auxiliary_mobile && SuspectScore.VpnMobile,
        methods.os_mismatch && SuspectScore.VpnOsMismatch,
        methods.relay && SuspectScore.VpnRelay,
      ].filter((key): key is SuspectScore => Boolean(key))
    }

    case 'ip-blocklist':
      return [
        e.ip_blocklist?.email_spam && SuspectScore.IpBlocklistEmailSpam,
        e.ip_blocklist?.attack_source && SuspectScore.IpBlocklistAttackSource,
      ].filter((key): key is SuspectScore => Boolean(key))

    case 'tor':
      return e.ip_blocklist?.tor_node === true ? [SuspectScore.Tor] : []

    case 'proxy': {
      if (e.proxy !== true) {
        return []
      }
      switch (e.proxy_details?.proxy_type) {
        case 'data_center':
          return [SuspectScore.Proxy]
        case 'residential':
          return weights.residentialProxyWeightsMode === 'confidence'
            ? [`residentialProxy${CONFIDENCE_KEY_SUFFIX[e.proxy_confidence ?? 'low']}`]
            : [SuspectScore.ResidentialProxy]
        default:
          return []
      }
    }

    case 'tampering':
      // The score uses the anomaly score threshold, not the `tampering` result.
      return [
        (e.tampering_details?.anomaly_score ?? 0) > 0.5 && SuspectScore.Tampering,
        e.tampering_details?.anti_detect_browser && SuspectScore.TamperingAntiDetectBrowser,
      ].filter((key): key is SuspectScore => Boolean(key))

    case 'high-activity':
      return e.high_activity_device === true ? [SuspectScore.HighActivity] : []
    case 'incognito':
      return e.incognito === true ? [SuspectScore.Incognito] : []
    case 'virtual-machine':
      return e.virtual_machine === true ? [SuspectScore.VirtualMachine] : []
    case 'privacy-settings':
      return e.privacy_settings === true ? [SuspectScore.PrivacySettings] : []
    case 'developer-tools':
      return e.developer_tools === true ? [SuspectScore.DeveloperTools] : []
    case 'emulator':
      return e.emulator === true ? [SuspectScore.Emulator] : []
    case 'simulator':
      return e.simulator === true ? [SuspectScore.Simulator] : []
    case 'cloned-app':
      return e.cloned_app === true ? [SuspectScore.AppCloners] : []
    case 'frida':
      return e.frida === true ? [SuspectScore.Frida] : []
    case 'jailbreak':
      return e.jailbroken === true ? [SuspectScore.Jailbreak] : []
    case 'rooted-app':
      return e.root_apps === true ? [SuspectScore.RootApps] : []
    case 'mitm-attack':
      return e.mitm_attack === true ? [SuspectScore.MobileMitmAttack] : []
    case 'device-rarity':
      return e.rare_device === true ? [SuspectScore.DeviceRarity] : []

    default:
      return []
  }
}

/**
 * Reconstructs how each Smart Signal contributed to the event's Suspect Score.
 * The score is a plain sum of the weights of the fired signals, so the breakdown is exact
 * as long as the workspace's weights have not changed since the event.
 *
 * Contributions come from the same raw fields the scoring uses, not from `isDetected`: the score
 * can count e.g. a VPN method or the tampering anomaly score even when the public result is false.
 */
export function calculateSuspectScoreBreakdown(
  event: IdEventV4,
  scores: Partial<Record<Platform, PlatformScores>>,
  weightsUpdatedAt?: string
): SuspectScoreBreakdown | undefined {
  const platform = SDK_PLATFORM_TO_WEIGHTS_PLATFORM[event.sdk?.platform ?? '']
  const weights = platform ? scores[platform] : undefined
  if (event.suspect_score == null || weights == null) {
    return undefined
  }

  const contributions = SMART_SIGNAL_DEFINITIONS.map((definition) => ({
    definition,
    contribution: firedWeightKeys(definition.key, event, weights).reduce((sum, key) => sum + (weights[key] ?? 0), 0),
  }))

  const contributing = contributions.filter((c) => c.contribution > 0).sort((a, b) => b.contribution - a.contribution)
  const total = contributing.reduce((sum, c) => sum + c.contribution, 0)

  const parsedUpdatedAt = weightsUpdatedAt ? DateTime.fromISO(weightsUpdatedAt) : undefined
  const updatedAt = parsedUpdatedAt?.isValid ? parsedUpdatedAt : undefined

  return {
    contributing,
    notContributing: contributions.filter((c) => c.contribution <= 0 && c.definition.isDetected(event)),
    total,
    reconciles: total === event.suspect_score,
    weightsChangedSinceEvent:
      updatedAt != null && event.timestamp < updatedAt.toMillis() + WEIGHTS_PROPAGATION_BUFFER_MS,
    weightsUpdatedAt: updatedAt?.toJSDate(),
  }
}
